import { Form, Select } from "antd";
import React, { useState, useEffect } from "react";
import { HelpBubble } from "~/components/help/helpBubble.jsx";
import { $t } from "~/i18n.js";
import { all_start_methods } from "~/redux/flights/flights.state.js";

/**
 * Custom hook for managing category-dependent start methods
 * @param {string} categoryFieldName - Name of the category field
 * @param {string} startMethodFieldName - Name of the start method field
 * @param {Object} formInstance - Optional form instance, will use useFormInstance if not provided
 * @returns {Array} Available start methods based on category
 */
export const useCategoryStartMethods = (categoryFieldName = "kist_category", startMethodFieldName = "start_methode", formInstance = null) => {
  const defaultForm = Form.useFormInstance();
  const form = formInstance || defaultForm;
  const [availableStartMethods, setAvailableStartMethods] = useState(all_start_methods);
  
  // Watch for category changes
  const categoryValue = Form.useWatch(categoryFieldName, formInstance ? { form: formInstance } : undefined);

  useEffect(() => {
    if (categoryValue === "sleep") {
      setAvailableStartMethods(["tmg", "sep"]);
      // Clear start_methode if current value is not in the new available methods
      const currentStartMethod = form?.getFieldValue(startMethodFieldName);
      if (currentStartMethod && !["tmg", "sep"].includes(currentStartMethod)) {
        form?.setFieldValue(startMethodFieldName, null);
      }
    } else {
      setAvailableStartMethods(all_start_methods.filter(m => m !== 'other'));
    }
  }, [categoryValue, form, startMethodFieldName]);

  return availableStartMethods;
};

/**
 * Reusable component for category and start method selection with dependent logic
 * @param {Object} props
 * @param {boolean} [props.readOnly=false] - Whether the fields should be read-only
 * @param {string} [props.categoryFieldName="kist_category"] - Name of the category field
 * @param {string} [props.startMethodFieldName="start_methode"] - Name of the start method field
 * @param {boolean} [props.showCategoryHelp=true] - Whether to show help bubble for category
 * @param {boolean} [props.showStartMethodHelp=true] - Whether to show help bubble for start method
 * @param {Object} [props.form] - Form instance to use, will use Form.useFormInstance if not provided
 */
export const CategoryStartMethodSelect = ({
  readOnly = false,
  categoryFieldName = "kist_category",
  startMethodFieldName = "start_methode",
  showCategoryHelp = true,
  showStartMethodHelp = true,
  form = null,
} = {}) => {
  const availableStartMethods = useCategoryStartMethods(categoryFieldName, startMethodFieldName, form);

  return (
    <>
      <Form.Item
        label={
          <p>
            {$t("Beschikbaar")}{" "}
            {showCategoryHelp && (
              <HelpBubble
                content={
                  <div>
                    <p>{$t("Bepaald inzetbaarheid door clubleden")}:</p>
                    <p>
                      <span className="bold">{$t("Club")}</span>:{" "}
                      {$t("Door clubleden worden ingezien en ingezet.")}
                    </p>
                    <p>
                      <span className="bold">{$t("Sleep")}</span>:{" "}
                      {$t("Door club worden ingezet als sleepkist.")}
                    </p>
                    <p>
                      <span className="bold">{$t("Prive")}</span>:{" "}
                      {$t("Alleen toegewezen leden hebben toegang.")}
                    </p>
                  </div>
                }
              />
            )}
          </p>
        }
        name={categoryFieldName}
        rules={[
          { required: true, message: $t("Selecteer beschikbaarheid") },
        ]}
      >
        <Select disabled={readOnly}>
          <Select.Option value="club">{$t("Club")}</Select.Option>
          <Select.Option value="sleep">{$t("Sleep")}</Select.Option>
          <Select.Option value="prive">{$t("Prive")}</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item
        label={
          <p>
            {$t("Startmethode")}{" "}
            {showStartMethodHelp && (
              <HelpBubble
                content={$t("Initieel geselecteerde startmethode")}
              />
            )}
          </p>
        }
        name={startMethodFieldName}
      >
        <Select disabled={readOnly}>
          <Select.Option value={null}>{$t("geen")}</Select.Option>
          {availableStartMethods.map((method, index) => (
            <Select.Option key={index} value={method}>
              {$t(method)}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
    </>
  );
};
